import { Container, Graphics, Text, Ticker } from 'pixi.js'
import { tween, Easing } from './animations'

const BUBBLE_PAD_X = 18
const BUBBLE_PAD_Y = 12
const BUBBLE_RADIUS = 20
const BUBBLE_MAX_WIDTH = 240
const TAIL_H = 14
const TYPEWRITER_MS_PER_CHAR = 35
const DISPLAY_HOLD_MS = 2500
const FADE_MS = 300

const BUBBLE_FILL = 0xffffff
const BUBBLE_STROKE = 0xc8d0da
const BUBBLE_STROKE_W = 1.5
const SHADOW_COLOR = 0x3a4a5c
const SHADOW_ALPHA = 0.10

const DEFAULT_PROMPTS = [
  'Got questions? Click me!',
  'I can guide you through this page',
  'Need help? Let\u2019s chat!',
]

export class SpeechBubble {
  readonly container: Container
  private bg: Graphics
  private label: Text
  private ticker: Ticker

  private prompts: string[]
  private promptIndex = 0
  private charIndex = 0
  private phase: 'idle' | 'typing' | 'holding' | 'fading' = 'idle'
  private phaseElapsed = 0
  private cancelFade: (() => void) | null = null
  private tickCb: ((dt: Ticker) => void) | null = null
  private running = false

  constructor(ticker: Ticker, prompts?: string[]) {
    this.ticker = ticker
    this.prompts = prompts?.length ? prompts : DEFAULT_PROMPTS

    this.container = new Container()
    this.container.alpha = 0
    this.container.visible = false

    this.bg = new Graphics()
    this.container.addChild(this.bg)

    this.label = new Text({
      text: '',
      style: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 13,
        fontWeight: '500',
        fill: 0x2a2a3e,
        wordWrap: true,
        wordWrapWidth: BUBBLE_MAX_WIDTH - BUBBLE_PAD_X * 2,
        lineHeight: 19,
      },
    })
    this.label.x = BUBBLE_PAD_X
    this.label.y = BUBBLE_PAD_Y
    this.container.addChild(this.label)

    // Make bubble interactive
    this.container.eventMode = 'static'
    this.container.cursor = 'pointer'
  }

  /** Start the typewriter + cycling animation. */
  start(): void {
    if (this.running) return
    this.running = true
    this.container.visible = true
    this.promptIndex = 0
    this.beginTyping()

    this.tickCb = (dt: Ticker) => this.update(dt.deltaMS)
    this.ticker.add(this.tickCb)
  }

  /** Stop cycling and hide. */
  stop(): void {
    if (!this.running) return
    this.running = false
    this.phase = 'idle'
    if (this.tickCb) this.ticker.remove(this.tickCb)
    this.tickCb = null
    this.cancelFade?.()
    this.cancelFade = null
    this.container.visible = false
    this.container.alpha = 0
  }

  private beginTyping(): void {
    this.charIndex = 0
    this.phaseElapsed = 0
    this.phase = 'typing'
    this.label.text = ''
    this.drawBubble()
    // Fade in at the start of each prompt
    this.container.alpha = 0
    this.cancelFade = tween({
      target: this.container,
      props: { alpha: 1 },
      duration: FADE_MS,
      easing: Easing.easeOutCubic,
      ticker: this.ticker,
    })
  }

  private update(deltaMs: number): void {
    if (this.phase === 'typing') {
      this.phaseElapsed += deltaMs
      const currentPrompt = this.prompts[this.promptIndex]
      const charsToShow = Math.min(
        Math.floor(this.phaseElapsed / TYPEWRITER_MS_PER_CHAR),
        currentPrompt.length,
      )

      if (charsToShow !== this.charIndex) {
        this.charIndex = charsToShow
        this.label.text = currentPrompt.substring(0, this.charIndex)
        this.drawBubble()
      }

      if (this.charIndex >= currentPrompt.length) {
        this.phase = 'holding'
        this.phaseElapsed = 0
      }
    } else if (this.phase === 'holding') {
      this.phaseElapsed += deltaMs
      if (this.phaseElapsed >= DISPLAY_HOLD_MS) {
        this.phase = 'fading'
        this.phaseElapsed = 0
        this.cancelFade?.()
        this.cancelFade = tween({
          target: this.container,
          props: { alpha: 0 },
          duration: FADE_MS,
          easing: Easing.easeInCubic,
          ticker: this.ticker,
          onComplete: () => {
            // Advance to next prompt
            this.promptIndex = (this.promptIndex + 1) % this.prompts.length
            this.beginTyping()
          },
        })
      }
    }
  }

  /**
   * Draw a cartoon-style speech bubble as a single unified path (body + tail),
   * so there is no seam between them.
   *
   *    ╭──────────────────╮
   *    │  Got questions?   │
   *    │  Click me!        │
   *    ╰───────╮──────────╯
   *             ╲
   *              •  (points toward avatar)
   */
  private drawBubble(): void {
    const textW = Math.max(this.label.width, 50)
    const textH = Math.max(this.label.height, 18)
    const w = textW + BUBBLE_PAD_X * 2
    const h = textH + BUBBLE_PAD_Y * 2
    const r = BUBBLE_RADIUS

    // Tail geometry — sits on the bottom edge of the bubble
    const tailW = 14
    const tailLeftX = w * 0.5 - tailW / 2   // left edge of tail gap
    const tailRightX = w * 0.5 + tailW / 2  // right edge of tail gap
    const tailTipX = w * 0.48               // tip slightly left for cartoon curve
    const tailTipY = h + TAIL_H

    this.bg.clear()

    // --- Shadow (offset) ---
    this.bg.roundRect(2, 3, w, h + 2, r)
    this.bg.fill({ color: SHADOW_COLOR, alpha: SHADOW_ALPHA })

    // --- Single unified path: body + tail ---
    // Start at top-left after the corner
    this.bg.moveTo(r, 0)
    // Top edge
    this.bg.lineTo(w - r, 0)
    // Top-right corner
    this.bg.quadraticCurveTo(w, 0, w, r)
    // Right edge
    this.bg.lineTo(w, h - r)
    // Bottom-right corner
    this.bg.quadraticCurveTo(w, h, w - r, h)
    // Bottom edge — right side up to tail gap
    this.bg.lineTo(tailRightX, h)
    // Tail: right side curve down to tip
    this.bg.quadraticCurveTo(tailRightX - 2, h + TAIL_H * 0.35, tailTipX, tailTipY)
    // Tail: left side curve back up
    this.bg.quadraticCurveTo(tailLeftX + 2, h + TAIL_H * 0.55, tailLeftX, h)
    // Bottom edge — left side
    this.bg.lineTo(r, h)
    // Bottom-left corner
    this.bg.quadraticCurveTo(0, h, 0, h - r)
    // Left edge
    this.bg.lineTo(0, r)
    // Top-left corner
    this.bg.quadraticCurveTo(0, 0, r, 0)
    this.bg.closePath()

    // Fill
    this.bg.fill({ color: BUBBLE_FILL, alpha: 0.97 })
    // Stroke the same unified path
    this.bg.stroke({ color: BUBBLE_STROKE, width: BUBBLE_STROKE_W, alpha: 0.6 })

    // Center the bubble horizontally
    this.container.pivot.x = w / 2
    this.container.pivot.y = h + TAIL_H
  }

  /** Register a click handler on the bubble. */
  onClick(handler: () => void): void {
    this.container.on('pointertap', handler)
  }

  destroy(): void {
    this.stop()
    this.container.destroy({ children: true })
  }
}
